import { useState, useEffect, useRef } from 'react';

export default function CommonIssues() {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) setIsVisible(true);
        });
      },
      { threshold: 0.1 }
    );

    const el = sectionRef.current;
    if (el) observer.observe(el);
    return () => { if (el) observer.unobserve(el); };
  }, []);

  const issues = [
    {
      title: 'Thiếu kiến trúc tổng thể',
      description: 'Triển khai nhiều hệ thống nhưng thiếu kiến trúc tổng thể, khó tích hợp và đồng bộ dữ liệu giữa các phòng ban.',
      icon: 'ri-stack-line',
      color: 'from-[#06b6d4] to-[#0891b2]',
      bgColor: 'from-[#06b6d4]/5 to-[#0891b2]/5',
    },
    {
      title: 'Chi phí cao, hiệu quả thấp',
      description: 'Đầu tư CNTT dàn trải, chi phí cao nhưng hiệu quả vận hành thấp, không đạt được mục tiêu kinh doanh đề ra.',
      icon: 'ri-funds-line',
      color: 'from-[#dc2626] to-[#b91c1c]',
      bgColor: 'from-[#dc2626]/5 to-[#b91c1c]/5',
    },
    {
      title: 'Phụ thuộc nhà cung cấp',
      description: 'Phụ thuộc vào nhà cung cấp, thiếu góc nhìn tư vấn độc lập và khách quan về giải pháp phù hợp.',
      icon: 'ri-links-line',
      color: 'from-[#f59e0b] to-[#d97706]',
      bgColor: 'from-[#f59e0b]/5 to-[#d97706]/5',
    },
    {
      title: 'Rủi ro bảo mật cao',
      description: 'Thiếu tiêu chuẩn bảo mật thống nhất, tiềm ẩn rủi ro an toàn thông tin và mất dữ liệu quan trọng.',
      icon: 'ri-shield-cross-line',
      color: 'from-[#ef4444] to-[#dc2626]',
      bgColor: 'from-[#ef4444]/5 to-[#dc2626]/5',
    },
    {
      title: 'Khó kiểm soát dự án',
      description: 'Khó kiểm soát tiến độ và chất lượng trong quá trình triển khai dự án, dẫn đến trễ deadline và vượt ngân sách.',
      icon: 'ri-time-line',
      color: 'from-[#8b5cf6] to-[#7c3aed]',
      bgColor: 'from-[#8b5cf6]/5 to-[#7c3aed]/5',
    },
  ];

  return (
    <section
      ref={sectionRef}
      id="services"
      className="py-24 bg-gradient-to-b from-white via-[#f8fafc] to-white"
    >
      <div className="max-w-7xl mx-auto px-6">
        <div
          className={`max-w-4xl mx-auto text-center mb-16 transition-all duration-1000 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <div className="inline-block mb-4">
            <span className="text-[11px] font-semibold tracking-[0.2em] text-[#dc2626] bg-[#dc2626]/10 px-4 py-2 rounded-full border border-[#dc2626]/20">
              PAIN POINTS
            </span>
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-[#1e293b] mb-4">
            Những thách thức bạn đang đối mặt
          </h2>
          <p className="text-lg text-[#64748b] max-w-2xl mx-auto">
            Chúng tôi hiểu rõ những khó khăn mà doanh nghiệp gặp phải trong hành trình chuyển đổi số
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {issues.map((issue, index) => (
            <div
              key={index}
              className={`group relative bg-gradient-to-br ${issue.bgColor} backdrop-blur-sm border border-[#e2e8f0] rounded-2xl p-8 hover:shadow-2xl hover:-translate-y-2 transition-all duration-500 cursor-pointer overflow-hidden ${
                index === 3 ? 'lg:col-span-2 md:col-span-2' : ''
              } ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
              style={{ transitionDelay: `${index * 100}ms` }}
            >
              {/* Decorative gradient overlay */}
              <div className={`absolute top-0 right-0 w-32 h-32 bg-gradient-to-br ${issue.color} opacity-0 group-hover:opacity-10 rounded-full blur-3xl transition-opacity duration-500 -mr-16 -mt-16`}></div>
              
              {/* Icon container */}
              <div className="relative mb-6">
                <div className={`w-16 h-16 bg-gradient-to-br ${issue.color} rounded-2xl flex items-center justify-center shadow-lg group-hover:scale-110 group-hover:rotate-6 transition-all duration-500`}>
                  <i className={`${issue.icon} text-3xl text-white`}></i>
                </div>
                {/* Decorative dot */}
                <div className={`absolute -top-1 -right-1 w-3 h-3 bg-gradient-to-br ${issue.color} rounded-full animate-pulse`}></div>
              </div>

              {/* Content */}
              <div className="relative">
                <h3 className="text-xl font-bold text-[#1e293b] mb-3 group-hover:text-[#2a4b7c] transition-colors duration-300">
                  {issue.title}
                </h3>
                <p className="text-sm text-[#64748b] leading-relaxed">
                  {issue.description}
                </p>
              </div>

              {/* Hover arrow indicator */}
              <div className="absolute bottom-6 right-6 w-8 h-8 flex items-center justify-center opacity-0 group-hover:opacity-100 transform translate-x-2 group-hover:translate-x-0 transition-all duration-300">
                <i className={`ri-arrow-right-up-line text-xl bg-gradient-to-br ${issue.color} bg-clip-text text-transparent`}></i>
              </div>

              {/* Bottom border accent */}
              <div className={`absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r ${issue.color} transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left rounded-b-2xl`}></div>
            </div>
          ))}
        </div>

        {/* Bottom CTA – delay 500ms như Skill Hub */}
        <div
          className={`text-center mt-16 transition-all duration-1000 delay-500 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <div className="inline-flex items-center space-x-2 text-sm text-[#64748b] bg-white px-6 py-3 rounded-full border border-[#e2e8f0] shadow-sm">
            <i className="ri-lightbulb-line text-[#f59e0b] text-lg"></i>
            <span>Chúng tôi có giải pháp cho từng thách thức này</span>
          </div>
        </div>
      </div>
    </section>
  );
}
